import Section from "@bmi-digital/components/section";
import { replaceSpaces, transformHyphens } from "@bmi-digital/components/utils";
import Villain from "@bmi-digital/components/villain";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { graphql } from "gatsby";
import React, { useMemo } from "react";
import Button from "@bmi-digital/components/button";
import RichText from "./RichText";
import { useSiteContext } from "./Site";
import {
  DescriptionGrid,
  DescriptionTypoMultiLine
} from "./styles/SyndicateSection.styles";
import createImageProps from "./image/createImageProps";
import createVideoProps from "./video/createVideoProps";
import { getCTA } from "./link/utils";
import type { MicroCopyValues } from "@bmi-digital/dxb-microcopies";
import type { VillainProps } from "@bmi-digital/components/villain";
import type { PageInfoVillain } from "./PageInfo";
import type { PromoVillain } from "./Promo";
import type { PayloadSyndicateSection } from "../types/payload";

export type SyndicateSectionData = Pick<
  PayloadSyndicateSection,
  "blockType" | "title" | "description" | "isReversed"
> & {
  villains: readonly { value: PromoVillain | PageInfoVillain }[];
};

export interface Props {
  data: SyndicateSectionData;
  position: number;
}

const SyndicateSection = ({
  data: { description, title, villains, isReversed },
  position
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { countryCode, getMicroCopy } = useSiteContext();

  const getCallToAction = (
    data: PromoVillain | PageInfoVillain,
    countryCode: string,
    getMicroCopy: (
      path: MicroCopyValues,
      variables?: Record<string, string>
    ) => string
  ) => {
    const cta = getCTA(
      data,
      countryCode,
      getMicroCopy(microCopy.PAGE_LINK_LABEL)
    );

    const label =
      data.collectionType === "promo" && data.cta && data.cta[0]
        ? data.cta[0].label
        : getMicroCopy(microCopy.PAGE_LINK_LABEL);

    if (cta) {
      return (
        <Button {...cta} variant="opaqueOutlined">
          {label}
        </Button>
      );
    }

    return null;
  };

  const villainsData = useMemo(
    () =>
      villains?.map((data) => {
        return {
          title: transformHyphens(data.value.title),
          children:
            "body" in data.value && data.value.body ? (
              <RichText document={data.value.body} hasNoBottomMargin />
            ) : (
              transformHyphens(data.value.subtitle)
            ),
          media:
            "featuredVideo" in data.value && data.value.featuredVideo
              ? createVideoProps(data.value.featuredVideo, theme)
              : data.value.featuredMedia
                ? createImageProps(
                    {
                      ...data.value.featuredMedia,
                      size: "cover",
                      isMobile
                    },
                    theme
                  )
                : undefined,
          cta: getCallToAction(data.value, countryCode, getMicroCopy)
        };
      }),
    [countryCode, getMicroCopy, isMobile, villains]
  );

  if (villainsData?.length === 1) {
    const { children, ...villainProperties } = villainsData[0];

    if (position === 0) {
      return (
        <Section
          backgroundColor="white"
          data-testid={`syndicate-section-${replaceSpaces(title)}`}
        >
          {title && <Section.Title>{title}</Section.Title>}
          {description && (
            <DescriptionGrid container lg={8} xs={12}>
              <DescriptionTypoMultiLine
                data-testid={"syndicate-section-description"}
              >
                {description}
              </DescriptionTypoMultiLine>
            </DescriptionGrid>
          )}
          <Villain {...villainProperties} isReversed={isReversed}>
            {children}
          </Villain>
        </Section>
      );
    }

    return (
      <Villain
        {...villainProperties}
        isFullWidth={true}
        isReversed={isReversed}
      >
        {children}
      </Villain>
    );
  }

  return (
    <Section
      backgroundColor="white"
      data-testid={`syndicate-section-${replaceSpaces(title)}`}
    >
      {title && <Section.Title>{title}</Section.Title>}
      {description && (
        <DescriptionGrid container lg={8} xs={12}>
          <DescriptionTypoMultiLine
            data-testid={"syndicate-section-description"}
          >
            {description}
          </DescriptionTypoMultiLine>
        </DescriptionGrid>
      )}
      {villainsData?.map(
        ({ children, ...villainProperties }: VillainProps, index) => (
          <Villain
            key={`${title}${index}`}
            {...villainProperties}
            isReversed={(index + Number(!isReversed)) % 2 === 0}
            isFullWidth={villains.length === 1}
          >
            {children}
          </Villain>
        )
      )}
    </Section>
  );
};

export default SyndicateSection;

export const query = graphql`
  fragment SyndicateSectionFragment on PayloadSyndicateSection {
    blockType
    title
    description
    villains {
      ... on PayloadPromoOrPage {
        value {
          ...PromoVillainFragment
          ...PageInfoVillainFragment
        }
      }
    }
    isReversed
  }
`;
